/**
 * THE PROVIDER APPLICATION QUEUE — the first working module screen.
 *
 *   "Nothing in the portal shows the submitted application for the provider
 *    workspace yet - is it not wired? it should show..."
 *
 * THE SHAPE OF THIS SCREEN IS CANON 4.2, NOT A LAYOUT PREFERENCE.
 *
 * The list shows what the database's queue function returns, which carries no
 * email, no phone, no owner name and no document. That is why a reviewer can
 * look at their own workload without capturing a reason each time. Opening one
 * application is a different act: the reason is a required argument of
 * fn_provider_application, and the log entry lands before the row is read.
 *
 * So this screen cannot accidentally disclose more than it should, because the
 * two calls return different things and only one of them takes a reason. The
 * component is not trusted with the boundary — it could not cross it.
 *
 * DECIDING NEEDS ITS OWN REASON, SEPARATELY.
 *
 * Reading a file and acting on it are different acts, and the reason for one is
 * not the reason for the other. "reviewing the queue" justifies opening; it
 * does not justify approving. fn_decide_provider_application asks again.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, PrimaryCTA, QuietButton, Notice, LoadingPanel, Spinner,
} from '../ui';
import {
  fetchApplicationQueue, openApplication, decideApplication, waitingLabel, waitTone,
} from '../lib/compliance';

const TABS = [
  { key: 'submitted', label: 'Waiting' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: null, label: 'All' },
];

const MIN_REASON = 8;

// ---------------------------------------------------------------------------

function ReasonPrompt({ title, body, confirmLabel, tone = 'green', busy, error, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length < MIN_REASON;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 60, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
      background: 'rgba(13,36,24,0.45)', fontFamily: SANS,
    }}>
      <div role="dialog" aria-modal="true" aria-label={title} style={{ width: '100%', maxWidth: 440 }}>
        <Card>
          <h2 style={{ fontSize: 15.5, fontWeight: 700, color: O.ink }}>{title}</h2>
          <p style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>{body}</p>

          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            autoFocus
            aria-label="Reason"
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 14, resize: 'none',
              padding: '12px 13px', borderRadius: 11, fontFamily: SANS, fontSize: 13.5,
              border: `1px solid ${O.hairlineOn}`, color: O.ink, background: '#FFFFFF',
            }}
          />
          {tooShort && reason.length > 0 && (
            <p style={{ marginTop: 6, fontSize: 11.5, color: O.inkFaint }}>
              A few more words — at least {MIN_REASON} characters.
            </p>
          )}

          {error && <div style={{ marginTop: 12 }}><Notice tone="critical">{error}</Notice></div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <QuietButton onClick={onCancel}>Cancel</QuietButton>
            <PrimaryCTA
              onClick={() => onConfirm(reason.trim())}
              busy={busy}
              disabled={busy || tooShort}
              style={tone === 'critical' ? { background: O.critical, boxShadow: 'none' } : undefined}
            >
              {confirmLabel}
            </PrimaryCTA>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: `1px solid ${O.hairline}` }}>
      <div style={{ flex: '0 0 150px', fontSize: 12, color: O.inkFaint }}>{label}</div>
      <div style={{ flex: 1, fontSize: 12.5, color: O.ink, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Detail({ app, onBack, onDecided }) {
  const [prompt, setPrompt] = useState(null);   // 'approve' | 'reject'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const decided = app.status === 'approved' || app.status === 'activated' || app.status === 'rejected';

  async function confirm(reason) {
    setBusy(true);
    setError(null);
    const res = await decideApplication({ id: app.id, decision: prompt, reason });
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    setPrompt(null);
    setOutcome(res.data);
    if (onDecided) onDecided();
  }

  return (
    <>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', padding: 0, font: 'inherit',
        fontSize: 12.5, color: O.green, cursor: 'pointer',
      }}>← Back to the queue</button>

      <div style={{ marginTop: 16 }}>
        <Card>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: O.ink }}>{app.business_name}</h2>
            <StatusPill tone={app.status === 'approved' ? 'green'
              : app.status === 'rejected' ? 'critical' : 'info'}>
              {app.status}
            </StatusPill>
          </div>

          <div style={{ marginTop: 14 }}>
            <Row label="Owner" value={app.owner_name} />
            <Row label="Email" value={app.email} />
            <Row label="Phone" value={app.phone} />
            <Row label="City" value={app.city} />
            <Row label="Trades" value={(app.categories || []).join(', ')} />
            <Row label="Registered business" value={app.registered_business} />
            <Row label="Registration number" value={app.registration_number} />
            <Row label="Business bank account" value={app.has_business_bank_account} />
            <Row label="Professional email" value={app.professional_email || app.has_professional_email} />
            <Row label="Website" value={app.website_url || app.has_website} />
            <Row label="Insurance" value={app.insurance_type
              ? `${app.insurance_type}${app.insurance_provider ? ` · ${app.insurance_provider}` : ''}`
              : null} />
            <Row label="Insurance expires" value={app.insurance_expiry} />
            <Row label="Certification" value={app.certification_type
              ? `${app.certification_type}${app.certification_number ? ` · ${app.certification_number}` : ''}`
              : null} />
            <Row label="Maturity score" value={app.business_maturity_score} />
            <Row label="Review notes" value={app.review_notes} />
            <Row label="Rejection reason" value={app.rejection_reason} />
          </div>

          {/*
            The applicant has no profile if they never chose Provider at the
            door. Approving would create a business nobody can sign in to, and
            the database refuses it -- but saying so BEFORE the button is
            pressed is the difference between a helpful screen and a surprising
            error.
          */}
          {!app.applicant_profile_id && !decided && (
            <div style={{ marginTop: 14 }}>
              <Notice tone="warning">
                No member profile uses {app.email}, so there is nobody to approve yet.
                They sign in with that address and choose Provider first.
              </Notice>
            </div>
          )}

          {outcome && (
            <div style={{ marginTop: 14 }}>
              <Notice tone={outcome.status === 'rejected' ? 'warning' : 'info'}>
                {outcome.message}
              </Notice>
            </div>
          )}

          {error && !prompt && (
            <div style={{ marginTop: 14 }}><Notice tone="critical">{error}</Notice></div>
          )}

          {!decided && !outcome && (
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <QuietButton onClick={() => { setError(null); setPrompt('reject'); }}>
                Reject
              </QuietButton>
              <PrimaryCTA
                onClick={() => { setError(null); setPrompt('approve'); }}
                disabled={!app.applicant_profile_id}
              >
                Approve
              </PrimaryCTA>
            </div>
          )}
        </Card>
      </div>

      <p style={{ marginTop: 16, fontSize: 11.5, lineHeight: 1.65, color: O.inkFaint }}>
        Approving admits this business to the platform. It does not certify their
        documents — insurance, CIPC registration and operating area stay unverified
        until Compliance verifies each one.
      </p>

      {prompt && (
        <ReasonPrompt
          title={prompt === 'approve' ? 'Approve this application?' : 'Reject this application?'}
          body={prompt === 'approve'
            ? 'The provider workspace opens immediately. Your reason is written to the append-only log and cannot be edited afterwards.'
            : 'The applicant reads this reason on their own application, so write it for them. It is also written to the append-only log.'}
          confirmLabel={prompt === 'approve' ? 'Approve' : 'Reject'}
          tone={prompt === 'approve' ? 'green' : 'critical'}
          busy={busy}
          error={error}
          onCancel={() => { setPrompt(null); setError(null); }}
          onConfirm={confirm}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

export default function ProviderApplications() {
  const [tab, setTab] = useState('submitted');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(null);   // the row awaiting a reason
  const [openBusy, setOpenBusy] = useState(false);
  const [openError, setOpenError] = useState(null);
  const [app, setApp] = useState(null);

  const load = useCallback(async (status) => {
    setRows(null);
    setError(null);
    const res = await fetchApplicationQueue(status);
    if (res.error) setError(res.error);
    else setRows(res.data);
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  async function confirmOpen(reason) {
    setOpenBusy(true);
    setOpenError(null);
    const res = await openApplication(opening.id, reason);
    setOpenBusy(false);
    if (res.error) { setOpenError(res.error); return; }
    setOpening(null);
    setApp(res.data);
  }

  if (app) {
    return (
      <Detail
        app={app}
        onBack={() => { setApp(null); load(tab); }}
        onDecided={() => load(tab)}
      />
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.label}
            onClick={() => setTab(t.key)}
            style={{
              padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
              fontFamily: SANS, fontSize: 12.5, fontWeight: 600,
              border: `1px solid ${tab === t.key ? O.green : O.hairlineOn}`,
              background: tab === t.key ? O.green : 'transparent',
              color: tab === t.key ? '#FFFFFF' : O.inkMuted,
            }}
          >{t.label}</button>
        ))}
      </div>

      {error && <Notice tone="critical">{error}</Notice>}
      {rows === null && !error && <LoadingPanel message="Reading the queue…" />}

      {rows && rows.length === 0 && !error && (
        <Card>
          <p style={{ fontSize: 13.5, color: O.ink }}>
            {tab === 'submitted' ? 'No applications are waiting.' : 'Nothing here.'}
          </p>
          <p style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            {tab === 'submitted'
              ? 'An empty queue is a complete answer, not a fault.'
              : 'Applications appear here once they reach this state.'}
          </p>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div style={{ display: 'grid', gap: 12 }}>
          {rows.map((r) => (
            <Card key={r.id}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9 }}>
                <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink }}>{r.business_name}</h3>
                <StatusPill tone={waitTone(r.waiting_days)}>
                  {waitingLabel(r.waiting_days)}
                </StatusPill>
                {r.registered_business === 'yes' && <StatusPill tone="quiet">Registered</StatusPill>}
              </div>

              <p style={{ marginTop: 6, fontSize: 12.5, color: O.inkMuted }}>
                {r.city} · {(r.categories || []).join(', ') || 'no trades listed'}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 }}>
                <span style={{ fontSize: 11.5, color: O.inkFaint }}>
                  Insurance document: {r.has_insurance_doc ? 'attached' : 'none'}
                </span>
                <span style={{ fontSize: 11.5, color: O.inkFaint }}>
                  Certification: {r.has_certification_doc ? 'attached' : 'none'}
                </span>
                <span style={{ fontSize: 11.5, color: O.inkFaint }}>
                  Documents submitted: {r.documents_submitted}
                </span>
                {r.business_maturity_score !== null && (
                  <span style={{ fontSize: 11.5, color: O.inkFaint }}>
                    Maturity: {r.business_maturity_score}
                  </span>
                )}
              </div>

              <div style={{ marginTop: 12, maxWidth: 240 }}>
                <QuietButton onClick={() => { setOpenError(null); setOpening(r); }}>
                  Open this application
                </QuietButton>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/*
        The list above deliberately shows no contact details. Opening one is a
        separate act on identifiable personal data, and the reason is a required
        argument of the database function -- this dialogue is how it gets asked,
        not where it gets enforced.
      */}
      {opening && (
        <ReasonPrompt
          title="Why are you opening this application?"
          body={`Opening ${opening.business_name} shows the applicant's name, email address, phone number and documents. Your reason is written to the append-only log before any of it is read.`}
          confirmLabel="Record and open"
          busy={openBusy}
          error={openError}
          onCancel={() => { setOpening(null); setOpenError(null); }}
          onConfirm={confirmOpen}
        />
      )}
    </>
  );
}

export { ReasonPrompt, Spinner };
